import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Alert,
  Chip,
  Autocomplete,
  TextField,
} from '@mui/material';
import axios from 'axios';
import { API_URL } from '../../../config/api';
import { useAuth } from '../../../context/AuthContext';

const EmployeeSelector = ({ selectedEmployee, onEmployeeChange }) => {
  const { getAuthHeaders } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${API_URL}/admin-onboarding-steps/employees-with-progress`,
        { headers: getAuthHeaders() }
      );
      setEmployees(response.data);
    } catch (err) {
      setError('Failed to fetch employees');
      console.error('Error fetching employees:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEmployeeChange = (event, newValue) => {
    if (newValue) {
      // Pass both employeeId and userId (handle null userId)
      onEmployeeChange({
        employeeId: newValue._id,
        userId: newValue.userId || null,
        employeeData: newValue
      });
    } else {
      // If no employee selected, pass null
      onEmployeeChange(null);
    }
  };

  // Find the selected employee object for Autocomplete
  const selectedEmployeeOption = employees.find(
    emp => emp._id === selectedEmployee?.employeeId
  ) || null;

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
        Onboarding Activities
      </Typography>
      <Autocomplete
        sx={{ mt: 2 }}
        options={employees}
        value={selectedEmployeeOption}
        onChange={handleEmployeeChange}
        getOptionLabel={(option) => `${option.firstName} - ${option.email}`}
        loading={loading}
        disabled={loading}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Select Employee"
            placeholder="Search employee by name or email..."
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? 'Loading...' : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, employee) => (
          <Box component="li" {...props} key={employee._id}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {employee.firstName} - {employee.email}
                </Typography>
              </Box>
              <Chip 
                label={`${employee.progress?.completedSteps || 0}/${employee.progress?.totalSteps || 10}`}
                size="small"
                color={employee.progress?.completedSteps === employee.progress?.totalSteps ? "success" : "default"}
                variant="outlined"
              />
            </Box>
          </Box>
        )}
        filterOptions={(options, { inputValue }) => {
          return options.filter((option) => {
            const searchTerm = inputValue.toLowerCase();
            const name = (option.firstName || '').toLowerCase();
            const email = (option.email || '').toLowerCase();
            return name.includes(searchTerm) || email.includes(searchTerm);
          });
        }}
        noOptionsText="No employees found"
      />
    </Box>
  );
};

export default EmployeeSelector; 